/* RESPONSIBLE TEAM: team-standalone */

import { tracked } from '@glimmer/tracking';
import {
  StandalonePlatformToIcon,
  type StandalonePlatform,
} from 'embercom/objects/standalone/constants';
import { StandalonePlatform as platforms } from 'embercom/objects/standalone/constants';
import { isPresent } from '@ember/utils';
import dataFormatMap from 'embercom/models/data/attributes/data-format-map';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';
import type Attribute from 'embercom/models/attribute';

export const REQUIRED_ATTRIBUTES = {
  zendeskUserFields: [
    {
      platformIdentifier: 'id',
      intercomAttribute: 'user_id',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
    {
      platformIdentifier: 'name',
      intercomAttribute: 'name',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
    {
      platformIdentifier: 'email',
      intercomAttribute: 'email',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
  ],
  salesforceUserFields: [
    {
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      platformIdentifier: 'Id',
      intercomAttribute: 'user_id',
      isMappable: true,
      intercomFieldType: 'string' as IntercomAttributeType,
      unique: true,
      filterable: true,
    },
    {
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      platformIdentifier: 'Name',
      intercomAttribute: 'name',
      isMappable: false,
      intercomFieldType: 'string' as IntercomAttributeType,
      filterable: true,
    },
    {
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      platformIdentifier: 'Email',
      intercomAttribute: 'email',
      isMappable: true,
      intercomFieldType: 'string' as IntercomAttributeType,
      filterable: true,
    },
  ],
  ticketFields: ['id', 'subject', 'description'],
  caseFields: ['id', 'subject', 'description', 'origin'],
  organizationFields: [
    {
      platformIdentifier: 'id',
      intercomAttribute: 'remote_company_id',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
    {
      platformIdentifier: 'name',
      intercomAttribute: 'name',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
  ],
};

export type FieldType =
  | 'text'
  | 'textarea'
  | 'checkbox'
  | 'date'
  | 'integer'
  | 'decimal'
  | 'regexp'
  | 'dropdown'
  | 'multiselect'
  | 'lookup'
  | 'tagger'
  | 'id'
  | 'string'
  | 'address'
  | 'email'
  | 'phone'
  | 'boolean'
  | 'datetime'
  | 'url'
  | 'double'
  | 'picklist'
  | 'priority'
  | 'tickettype';

type IntercomAttributeType = 'string' | 'integer' | 'float' | 'boolean' | 'date';

export interface UserFieldResponse {
  id: string;
  name: string;
  description?: string;
  type: FieldType;
  required: boolean;
  is_mappable?: boolean;
  intercom_cda?: {
    identifier: string;
    type: IntercomAttributeType;
  };
  intercom_sda?: {
    identifier: string;
    type: IntercomAttributeType;
  };
  unique?: boolean;
  filterable?: boolean;
  createable?: boolean;
  updateable?: boolean;
}

export interface TicketFieldResponse {
  id: string;
  name: string;
  description?: string;
  type: FieldType;
  required: boolean;
  createable?: boolean;
  updateable?: boolean;
  intercom_cvda?: {
    id: string;
  };
}

export interface FieldInformationTooltip {
  name: string;
  description?: string;
  icon?: string;
  required: boolean;
  createable: boolean;
  updateable: boolean;
  synced: boolean;
  category: string;
  cvdaId: string;
  tooltipText?: string;
}

export interface OrganizationFieldResponse {
  id: string;
  name: string;
  description?: string;
  type: FieldType;
  required: boolean;
  intercom_cda?: {
    identifier: string;
    type: IntercomAttributeType;
  };
  intercom_sda?: {
    identifier: string;
    type: IntercomAttributeType;
  };
}

export class UserField {
  id: string;
  name: string;
  description?: string;
  type: FieldType;
  required: boolean;
  isMappable?: boolean;
  @tracked isSynchronizing?: boolean = false;
  intercom_cda?: {
    identifier: string;
    type: IntercomAttributeType;
  };
  intercom_sda?: {
    identifier: string;
    type: IntercomAttributeType;
  };
  unique?: boolean;
  filterable?: boolean;
  createable?: boolean;
  updateable?: boolean;

  constructor(response: UserFieldResponse) {
    this.id = response.id;
    this.name = response.name;
    this.description = response.description;
    this.type = response.type;
    this.required = response.required;
    this.isMappable = response.is_mappable;
    this.intercom_cda = response.intercom_cda;
    this.intercom_sda = response.intercom_sda;
    this.unique = response.unique;
    this.filterable = response.filterable;
    this.createable = response.createable;
    this.updateable = response.updateable;
  }

  get builtIn() {
    return this.required;
  }

  isAutoSynced(platformKey: string) {
    if (platformKey === platforms.Salesforce) {
      return REQUIRED_ATTRIBUTES.salesforceUserFields
        .map((f: any) => f.platformIdentifier)
        .includes(this.id);
    }

    return REQUIRED_ATTRIBUTES.zendeskUserFields
      .map((f: any) => f.platformIdentifier)
      .includes(this.id);
  }
}

export class OrganizationField {
  id: string;
  name: string;
  description?: string;
  type: FieldType;
  required: boolean;
  @tracked isSynchronizing?: boolean = false;
  intercom_cda?: {
    identifier: string;
    type: IntercomAttributeType;
  };
  intercom_sda?: {
    identifier: string;
    type: IntercomAttributeType;
  };

  constructor(response: OrganizationFieldResponse) {
    this.id = response.id;
    this.name = response.name;
    this.description = response.description;
    this.type = response.type;
    this.required = response.required;
    this.intercom_cda = response.intercom_cda;
    this.intercom_sda = response.intercom_sda;
  }

  get builtIn() {
    return this.required;
  }

  get intercomCdaIdentifier() {
    return this.intercom_cda?.identifier;
  }

  get intercomSdaIdentifier() {
    return this.intercom_sda?.identifier;
  }

  get synchronized() {
    return isPresent(this.intercom_cda?.identifier);
  }

  get attributeDataType() {
    return this.intercom_cda?.type || this.intercom_sda?.type;
  }

  isAutoSynced(_platformKey: string) {
    return REQUIRED_ATTRIBUTES.organizationFields
      .map((f: any) => f.platformIdentifier)
      .includes(this.id);
  }
}

export class TicketField {
  id: string;
  name: string;
  description?: string;
  type: FieldType;
  required: boolean;
  createable?: boolean;
  updateable?: boolean;
  @tracked isSynchronizing?: boolean = false;
  intercom_cvda?: {
    id: string;
  };

  constructor(response: TicketFieldResponse) {
    this.id = response.id;
    this.name = response.name;
    this.description = response.description;
    this.type = response.type;
    this.required = response.required;
    this.intercom_cvda = response.intercom_cvda;
    this.createable = response.createable;
    this.updateable = response.updateable;
  }

  get builtIn() {
    return this.required;
  }

  isAutoSynced(platformKey: string) {
    if (platformKey === platforms.Salesforce) {
      return REQUIRED_ATTRIBUTES.caseFields.includes(this.id);
    }

    return REQUIRED_ATTRIBUTES.ticketFields.includes(this.id);
  }
}

export class StandaloneUserAttribute {
  isSyncedWithPlatform: boolean;
  standalonePlatform?: StandalonePlatform;
  userField?: UserField;
  icon: string;
  name: string;
  intercomAttribute: any;
  formatName?: string;

  constructor({
    standalonePlatform,
    intercomAttribute,
    userField,
  }: {
    standalonePlatform?: StandalonePlatform;
    intercomAttribute?: any;
    userField?: UserField;
  }) {
    this.isSyncedWithPlatform = isPresent(standalonePlatform);
    this.standalonePlatform = standalonePlatform;
    this.intercomAttribute = intercomAttribute;
    this.userField = userField;
    this.name = userField?.name || intercomAttribute?.name;
    this.icon = isPresent(standalonePlatform)
      ? StandalonePlatformToIcon[standalonePlatform!]
      : intercomAttribute?.icon;
    if (userField && userField.required && userField.intercom_sda) {
      this.formatName =
        dataFormatMap[userField.intercom_sda.type as keyof typeof dataFormatMap] ||
        'settings.data-format-map.unknown';
    }
  }
}

export class StandaloneCompanyAttribute {
  isSyncedWithPlatform: boolean;
  standalonePlatform?: StandalonePlatform;
  field?: OrganizationField | ExternalFieldIdentifier;
  icon: string;
  name: string;
  intercomAttribute?: Attribute;
  formatName?: string;

  constructor({
    standalonePlatform,
    intercomAttribute,
    field,
  }: {
    standalonePlatform?: StandalonePlatform;
    intercomAttribute?: Attribute;
    field?: OrganizationField | ExternalFieldIdentifier;
  }) {
    this.isSyncedWithPlatform = isPresent(standalonePlatform);
    this.standalonePlatform = standalonePlatform;
    this.intercomAttribute = intercomAttribute;
    this.field = field;
    this.name = field?.name || intercomAttribute!.name;
    this.icon =
      (isPresent(standalonePlatform) && StandalonePlatformToIcon[standalonePlatform!]) ||
      intercomAttribute!.icon;

    if (field && field.builtIn && field.intercomSdaIdentifier) {
      let dataType: string | undefined;
      if ('intercom_sda' in field) {
        dataType = field.intercom_sda?.type;
      } else {
        dataType = field.type;
      }
      this.formatName =
        dataFormatMap[dataType as keyof typeof dataFormatMap] || 'settings.data-format-map.unknown';
    }
  }
}

export class StandaloneTicketAttribute {
  standalonePlatform: StandalonePlatform;
  ticketField: TicketField;
  intercomCvda?: any;
  icon: string | undefined;
  name: string;

  constructor({
    standalonePlatform,
    ticketField,
    intercomCvda,
  }: {
    standalonePlatform: StandalonePlatform;
    ticketField: TicketField;
    intercomCvda?: any;
  }) {
    this.standalonePlatform = standalonePlatform;
    this.ticketField = ticketField;
    this.name = ticketField.name;
    this.icon = StandalonePlatformToIcon[standalonePlatform];
    this.intercomCvda = intercomCvda;
  }
}
